import type { TaskItem } from '../api/types';
import type { CalendarEvent } from '../api/client';

function norm(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9\s'-]/g, '').trim();
}

export function fuzzyNameMatch(a: string, b: string): boolean {
  const na = norm(a);
  const nb = norm(b);
  if (!na || !nb) return false;
  if (na === nb) return true;
  const wa = na.split(/\s+/);
  const wb = nb.split(/\s+/);
  for (const x of wa) {
    if (x.length >= 3 && wb.includes(x)) return true;
  }
  const fa = wa[0];
  const fb = wb[0];
  if (fa.length >= 3 && fb.length >= 3 && (fa.startsWith(fb) || fb.startsWith(fa))) {
    return true;
  }
  return false;
}

function tokensFromEvent(e: CalendarEvent): string[] {
  const out: string[] = [];
  const m = e.title?.match(/(?:with|w\/)\s+([A-Za-z][A-Za-z'-]+(?:\s+[A-Za-z][A-Za-z'-]+)?)/);
  if (m) out.push(m[1]);
  for (const a of e.attendees ?? []) {
    const local = a.includes('@') ? a.split('@')[0] : a;
    const name = local.split(/[._-]/)[0];
    if (name) out.push(name);
  }
  return out;
}

function peopleFromTasks(tasks: TaskItem[]): string[] {
  const seen = new Map<string, string>();
  const add = (raw?: string | null) => {
    if (!raw) return;
    const key = norm(raw);
    if (!key) return;
    const existing = seen.get(key);
    if (!existing || raw.length > existing.length) seen.set(key, raw.trim());
  };
  for (const t of tasks) {
    (t.bringUpWith ?? []).forEach(add);
    add(t.relayTarget);
    for (const tag of t.tags ?? []) {
      if (/^[A-Z][a-z]+(\s+[A-Z][a-z]+)?$/.test(tag.trim())) add(tag);
    }
  }
  return Array.from(seen.values());
}

export interface ResolvedPerson {
  display: string;
  matchesField: (field: string) => boolean;
}

export function resolveMeetingPerson(
  event: CalendarEvent,
  tasks: TaskItem[],
): ResolvedPerson | null {
  const all = resolveMeetingPeople(event, tasks);
  return all.length > 0 ? all[0] : null;
}

export function resolveMeetingPeople(
  event: CalendarEvent,
  tasks: TaskItem[],
): ResolvedPerson[] {
  const tokens = tokensFromEvent(event);
  if (tokens.length === 0) return [];

  const directory = peopleFromTasks(tasks);
  const seen = new Map<string, { display: string; tokens: string[] }>();

  for (const token of tokens) {
    let best: string | null = null;
    for (const person of directory) {
      if (fuzzyNameMatch(token, person)) {
        if (!best || person.length > best.length) best = person;
      }
    }
    if (!best) {
      const t = token.trim();
      best = t.charAt(0).toUpperCase() + t.slice(1);
    }
    const key = norm(best);
    const entry = seen.get(key);
    if (entry) {
      entry.tokens.push(token);
    } else {
      seen.set(key, { display: best, tokens: [token] });
    }
  }

  return Array.from(seen.values()).map(({ display, tokens: toks }) => ({
    display,
    matchesField: (field: string) =>
      fuzzyNameMatch(field, display) || toks.some((tok) => fuzzyNameMatch(field, tok)),
  }));
}
